import { Button, Checkbox, Flex, SkeletonText, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { useRef, useState } from "react";
import { Admin_Labels_Eng } from "src/common/constants/englishLabels";
import { Admin_Labels_Hindi } from "src/common/constants/hindiLabels";
import Not_Found_Icon from "src/resources/icons/Not_Found_Icon.svg";
import React from "react";
import { RootState } from "src/store/store";
import { UserDetails } from "src/types/userDetails";
import { useSelector } from 'react-redux';
import RejectReasonModal from './RejectReasonModal';

export type PendingApprovalsProps = {
  userDetailsList: UserDetails[];
  isHindiLabels: boolean;
  headers: string[];
  hindiHeaders: string[];
  approveAllBackgroundColor: string;
  rejectAllBackgroundColor: string;
  rejectAllFontColor: string;
  rejectAllBorderColor: string; 
  checkList: (string | String)[];
  isAllChecked: boolean;
  handleMasterCheckboxChange: (e: { target: { checked: boolean } }) => void;
  checkedItems: boolean[];
  handleRowCheckboxChange: (
    e: any,
    index: number,
    data: string | String
  ) => void;
  onRowClick: (user: UserDetails, index: number) => void;
  approveUserApplication: (users: UserDetails[], action: "Approved" | "Rejected", reason?: string) => Promise<void> | void;
};

export const PendingApprovals: React.FC<PendingApprovalsProps> = (props) => {
  const {
    userDetailsList,
    isHindiLabels,
    headers,
    hindiHeaders,
    approveAllBackgroundColor,
    rejectAllBackgroundColor,
    rejectAllFontColor,
    rejectAllBorderColor,
    isAllChecked,
    handleMasterCheckboxChange,
    checkedItems,
    handleRowCheckboxChange,
    onRowClick,
    approveUserApplication,
  } = props;

  const { isProfilesLoading } = useSelector((state: RootState) => state.adminInfo);
  const leastRef = useRef<HTMLButtonElement | null>(null);

  // Local state for rejection modal
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [pendingRejectUsers, setPendingRejectUsers] = useState<UserDetails[]>([]);

  const openRejectModalFor = (users: UserDetails[]) => {
    if (!users || users.length === 0) return; // guard: require selection
    setPendingRejectUsers(users);
    setRejectReason("");
    setIsRejectOpen(true);
  };
  const closeRejectModal = () => setIsRejectOpen(false);
  const confirmReject = async () => {
    if (!rejectReason.trim()) return;
    await approveUserApplication(pendingRejectUsers, "Rejected", rejectReason.trim());
    closeRejectModal();
  };

  // Local state for approval modal (optional reason)
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [approveReason, setApproveReason] = useState("");
  const [pendingApproveUsers, setPendingApproveUsers] = useState<UserDetails[]>([]);

  const openApproveModalFor = (users: UserDetails[]) => {
    if (!users || users.length === 0) return; // guard: require selection
    setPendingApproveUsers(users);
    setApproveReason("");
    setIsApproveOpen(true);
  };
  const closeApproveModal = () => setIsApproveOpen(false);
  const confirmApprove = async () => {
    await approveUserApplication(pendingApproveUsers, "Approved", approveReason.trim() || undefined);
    closeApproveModal();
  };

  const getSelectedUsers = () =>
  userDetailsList.filter((_, i) => checkedItems[i]);

  return (
    <Flex direction="column" alignItems="center" alignContent="space-around">
      {/* Rejection Reason Modal */}
      <RejectReasonModal
        isOpen={isRejectOpen}
        onClose={closeRejectModal}
        leastRef={leastRef}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        onConfirm={confirmReject}
      />

      <RejectReasonModal
        isOpen={isApproveOpen}
        onClose={closeApproveModal}
        leastRef={leastRef}
        rejectReason={approveReason}
        setRejectReason={setApproveReason}
        onConfirm={confirmApprove}
        title="Reason for Acceptance"
        description="Optionally provide a reason for accepting this user."
        submitLabel="Confirm Approval"
        requireReason={false}
        quickTags={["Verified", "Complete Documents", "Meets Criteria"]}
      />

      {userDetailsList.length !== 0 ? (
        <>
          <Flex direction={{ base: "column", md: "row" }} justifyContent="space-between" width="85%">
            <Text style={{ fontSize: "34px", fontWeight: "600" }}>
             {isHindiLabels ? Admin_Labels_Hindi.PENDING_APPROVAL_REQUESTS : Admin_Labels_Eng.PENDING_APPROVAL_REQUESTS}
            </Text>
            <Flex justifyContent="space-between">
              <Button
                isLoading={isProfilesLoading}
                style={{
                  paddingLeft: "21px",
                  paddingTop: "16px",
                  paddingRight: "21px",
                  paddingBottom: "16px",
                  fontSize: "20px",
                  backgroundColor: approveAllBackgroundColor,
                  borderRadius: "4px",
                  marginRight: "12px",
                  color: "white",
                }}
                onClick={()=>openApproveModalFor(getSelectedUsers())}
                disabled={!checkedItems.some(Boolean)}
              >
                {isHindiLabels ? Admin_Labels_Hindi.APPROVE_ALL : Admin_Labels_Eng.APPROVE_ALL}
              </Button>
              <Button
                isLoading={isProfilesLoading}
                style={{
                  paddingLeft: "21px",
                  paddingTop: "16px",
                  paddingRight: "21px",
                  paddingBottom: "16px",
                  fontSize: "20px",
                  backgroundColor: rejectAllBackgroundColor,
                  borderRadius: "4px",
                  border: rejectAllBorderColor,
                  color: rejectAllFontColor,
                }}
                onClick={() => openRejectModalFor(userDetailsList.filter((_, i) => checkedItems[i]))}
                disabled={!checkedItems.some(Boolean)}
              >
                {isHindiLabels ? Admin_Labels_Hindi.REJECT_ALL : Admin_Labels_Eng.REJECT_ALL}
              </Button>
            </Flex>
          </Flex>

          <TableContainer border="1px" borderColor="#EBEBEB" marginTop="15px" width="85%">
            <Table variant="simple">
              <Thead backgroundColor="#F4F4F4">
                <Tr>
                  <Th paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px">
                    <Checkbox
                      isChecked={isAllChecked}
                      onChange={handleMasterCheckboxChange}
                      sx={{
                        "& .chakra-checkbox__control": { borderRadius: "0", borderColor: "#FF7E6B" },
                        "& .chakra-checkbox__control[data-checked]": { bg: "#FF7E6B", borderColor: "#FF7E6B" },
                        "& .chakra-checkbox__control[data-checked]:hover": { bg: "#FF7E6B", borderColor: "#FF7E6B" },
                        "& .chakra-checkbox__control:hover": { bg: "#FF7E6B" },
                      }}
                    />
                  </Th>
                  {(isHindiLabels ? hindiHeaders : headers).map((data) => (
                    <Th key={data} paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px" textTransform="capitalize" fontSize="20px">
                      {data}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              {isProfilesLoading && (
                <Tbody>
                  <Tr>
                    <Td colSpan={7}>
                      <SkeletonText noOfLines={6} spacing='4' skeletonHeight='40px' />
                    </Td>
                  </Tr>
                </Tbody>
              )}
              <Tbody>
                {!isProfilesLoading && userDetailsList.map((data, index) => (
                  <Tr key={data?.communicationDetails?.mobileNumber || index}>
                    <Td paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px">
                      <Checkbox
                        sx={{
                          "& .chakra-checkbox__control": { borderRadius: "0", borderColor: "#FF7E6B" },
                          "& .chakra-checkbox__control[data-checked]": { bg: "#FF7E6B", borderColor: "#FF7E6B" },
                          "& .chakra-checkbox__control[data-checked]:hover": { bg: "#FF7E6B", borderColor: "#FF7E6B" },
                          "& .chakra-checkbox__control:hover": { bg: "#FF7E6B" },
                        }}
                        isChecked={checkedItems[index]}
                        onChange={(e) => handleRowCheckboxChange(e, index, data?.communicationDetails?.mobileNumber)}
                      />
                    </Td>
                    <Td
                      paddingLeft="12px"
                      paddingRight="12px"
                      paddingTop="20px"
                      paddingBottom="20px"
                      onClick={() => onRowClick(data, index)}
                    >
                      <Text _hover={{ textColor: "#FF7E6B", textDecoration: "underline", cursor: "pointer" }}>
                        <span style={{ fontSize: "16px" }}>{`${data?.personalDetails?.firstName}`}</span>
                      </Text>
                    </Td>
                    <Td paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px">
                      <span style={{ fontSize: "16px" }}>{data?.personalDetails?.joiningInterest || "—"}</span>
                    </Td>
                    <Td paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px">
                      <span style={{ fontSize: "16px" }}>{data?.communicationDetails?.mobileNumber}</span>
                    </Td>
                    <Td paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px">
                      <span style={{ fontSize: "16px" }}>{data?.personalDetails?.dob}</span>
                    </Td>
                    <Td paddingLeft="12px" paddingRight="12px" paddingTop="20px" paddingBottom="20px">
                      <button
                        style={{ padding: "12px", backgroundColor: "#E35E00", border: "1px", borderColor: "#E35E00", borderRadius: "4px", marginRight: "10px", color: "white" }}
                        onClick={() => openApproveModalFor([data])}
                      >
                        {isHindiLabels ? Admin_Labels_Hindi.APPROVE : Admin_Labels_Eng.APPROVE}
                      </button>
                      <button
                        style={{ padding: "12px", backgroundColor: "white", border: "2px solid #E35E00", borderColor: "#E35E00", borderRadius: "4px", color: "#E35E00" }}
                        onClick={() => openRejectModalFor([data])}
                      >
                        {isHindiLabels ? Admin_Labels_Hindi.REJECT : Admin_Labels_Eng.REJECT}
                      </button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Flex direction="column" justifyContent="center" alignItems="center" mt="1rem" gap="1.5rem">
          <Text color="#b4b9bd" fontSize="34px" fontWeight="500"> {isHindiLabels? Admin_Labels_Hindi.No_User_Approval_Pending: Admin_Labels_Eng.No_User_Approval_Pending}</Text>
          <img width={72} height={72} src={Not_Found_Icon} alt="Not found" />
        </Flex>
      )}
    </Flex>
  );
};

export default PendingApprovals;
